import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import CheckBox from 'commons/src/components/input/Checkbox';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import { PredefinedProperty, PlacementType } from 'commons/src/models/commonEnums';
import { IndoorSpace } from '../../../../../models/spaceModels';
import { Store } from '../../../../../reducers';
import { getNamedSpacePropertiesWithValue } from '../../../../spaces/properties/SpacePropertyEditCommon';
import { spacesSelector } from '../../../../spaces/space/spaceSelectors';
import styles from '../../iaqInsight2.module.scss';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    selectedSpaceIds: string[];
    onSave: (spaceIds: string[]) => void;
    locationId: string;
};

const SpaceSelectionModal: React.FC<Props> = ({ isOpen, onClose, selectedSpaceIds, onSave, locationId }) => {
    const { t: txt } = useTranslation();
    const { spaces: spacesData, request: spacesRequest } = useSelector((state: Store) =>
        spacesSelector(state, locationId)
    );
    const spacesLoading = spacesRequest?.loading ?? false;
    const { spacePropDefs: propertyDefinitions } = useSelector((state: Store) => state.config);
    const [searchText, setSearchText] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedSpaceIds);

    const indoorSpaces = useMemo(() => {
        return (spacesData?.spaces || []).filter(
            (space: { placement: PlacementType }) => space.placement === PlacementType.ROOM
        ) as IndoorSpace[];
    }, [spacesData]);

    const spacesWithProps = useMemo(() => {
        return indoorSpaces.map(space => {
            const spaceVariables = getNamedSpacePropertiesWithValue(space.properties, propertyDefinitions);
            return { ...space, spaceVariables };
        });
    }, [indoorSpaces, propertyDefinitions]);

    const filteredSpaces = useMemo(() => {
        if (!searchText) return spacesWithProps;
        const searchLower = searchText.toLowerCase();
        return spacesWithProps.filter(space => {
            const floor = space.spaceVariables[PredefinedProperty.Floor] || '';
            const name = space.name.toLowerCase();
            return name.includes(searchLower) || floor.toString().includes(searchLower);
        });
    }, [spacesWithProps, searchText]);

    const availableSpaces = useMemo(() => {
        return filteredSpaces.filter(space => !tempSelectedIds.includes(space.id));
    }, [filteredSpaces, tempSelectedIds]);

    const selectedSpaces = useMemo(() => {
        return spacesWithProps.filter(space => tempSelectedIds.includes(space.id));
    }, [spacesWithProps, tempSelectedIds]);

    const [leftSelected, setLeftSelected] = useState<string[]>([]);
    const [rightSelected, setRightSelected] = useState<string[]>([]);

    const handleLeftCheckboxChange = (spaceId: string, checked: boolean): void => {
        if (checked) {
            setLeftSelected([...leftSelected, spaceId]);
        } else {
            setLeftSelected(leftSelected.filter(id => id !== spaceId));
        }
    };

    const handleRightCheckboxChange = (spaceId: string, checked: boolean): void => {
        if (checked) {
            setRightSelected([...rightSelected, spaceId]);
        } else {
            setRightSelected(rightSelected.filter(id => id !== spaceId));
        }
    };

    const handleAdd = (): void => {
        const newSelected = [...tempSelectedIds, ...leftSelected];
        setTempSelectedIds(newSelected);
        setLeftSelected([]);
    };

    const handleRemove = (): void => {
        const newSelected = tempSelectedIds.filter(id => !rightSelected.includes(id));
        setTempSelectedIds(newSelected);
        setRightSelected([]);
    };

    const handleSave = (): void => {
        onSave(tempSelectedIds);
        onClose();
    };

    const handleClose = (): void => {
        setTempSelectedIds(selectedSpaceIds);
        setLeftSelected([]);
        setRightSelected([]);
        setSearchText('');
        onClose();
    };

    const getSpaceDisplayName = (space: IndoorSpace & { spaceVariables: { [key: string]: string } }): string => {
        const floor = space.spaceVariables[PredefinedProperty.Floor] || '';
        return floor ? `Floor ${floor} ${space.name}` : space.name;
    };

    const getSpaceTag = (space: IndoorSpace & { spaceVariables: { [key: string]: string } }): string | null => {
        const roomType = space.spaceVariables[PredefinedProperty.RoomType];
        return roomType || null;
    };

    const customHeader = (
        <div className={styles.customHeader}>
            <h1 className={styles.headerTitle}>{txt('IAQReport2.CustomSelection')}</h1>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
                <MaterialIcon name="close" />
            </button>
        </div>
    );

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={handleClose}
            size="large"
            customHeader={customHeader}
            className={styles.customSelectionModal}
        >
            <div className={styles.modalContent}>
                <div className={styles.columnsContainer}>
                    <div className={styles.column}>
                        <h3 className={styles.columnHeader}>{txt('Spaces.AllSpaces')}</h3>
                        <div className={styles.allSpacesBox}>
                            <div className={styles.searchContainer}>
                                <div className={styles.searchInputWrapper}>
                                    <MaterialIcon name="search" extraClass={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder={txt('Search')}
                                        value={searchText}
                                        onChange={(e): void => setSearchText(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className={styles.spaceList}>
                                {((): React.ReactNode => {
                                    if (spacesLoading) {
                                        return (
                                            <div className={styles.emptyMessage}>{txt('IAQReport2.LoadingSpaces')}</div>
                                        );
                                    }
                                    if (availableSpaces.length === 0 && indoorSpaces.length === 0) {
                                        return (
                                            <div className={styles.emptyMessage}>{txt('SpaceFloorPlan.NoSpaces')}</div>
                                        );
                                    }
                                    if (availableSpaces.length === 0) {
                                        return (
                                            <div className={styles.emptyMessage}>
                                                {txt('IAQReport2.AllSpacesInSelection')}
                                            </div>
                                        );
                                    }
                                    return availableSpaces.map(space => {
                                        const tag = getSpaceTag(space);
                                        return (
                                            <div key={space.id} className={styles.spaceItem}>
                                                <CheckBox
                                                    id={`left-${space.id}`}
                                                    checked={leftSelected.includes(space.id)}
                                                    onChange={(e): void =>
                                                        handleLeftCheckboxChange(space.id, e.currentTarget.checked)
                                                    }
                                                    label={
                                                        <div className={styles.spaceLabel}>
                                                            <span>{getSpaceDisplayName(space)}</span>
                                                            {tag && <span className={styles.tag}>{tag}</span>}
                                                        </div>
                                                    }
                                                    labelIsElement
                                                />
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                        <div className={styles.columnButtonContainer}>
                            <PrimaryButton
                                title={txt('Add')}
                                onClick={handleAdd}
                                disabled={leftSelected.length === 0}
                                color="primary"
                                fullWidth
                                trailingIcon={<MaterialIcon name="arrow_forward" />}
                            />
                        </div>
                    </div>
                    <div className={styles.column}>
                        <h3 className={styles.columnHeader}>
                            {txt('IAQReport2.SelectedSpaces')} ({tempSelectedIds.length})
                        </h3>
                        <div className={styles.selectedSpacesBox}>
                            <div className={styles.spaceList}>
                                {selectedSpaces.length === 0 ? (
                                    <div className={styles.emptyMessage}>{txt('IAQReport2.NoSpacesSelected')}</div>
                                ) : (
                                    selectedSpaces.map(space => {
                                        const tag = getSpaceTag(space);
                                        return (
                                            <div key={space.id} className={styles.spaceItem}>
                                                <CheckBox
                                                    id={`right-${space.id}`}
                                                    checked={rightSelected.includes(space.id)}
                                                    onChange={(e): void =>
                                                        handleRightCheckboxChange(space.id, e.currentTarget.checked)
                                                    }
                                                    label={
                                                        <div className={styles.spaceLabel}>
                                                            <span>{getSpaceDisplayName(space)}</span>
                                                            {tag && <span className={styles.tag}>{tag}</span>}
                                                        </div>
                                                    }
                                                    labelIsElement
                                                />
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        <div className={styles.columnButtonContainer}>
                            <PrimaryButton
                                title={txt('Remove')}
                                onClick={handleRemove}
                                disabled={rightSelected.length === 0}
                                color="secondary"
                                fullWidth
                                icon={<MaterialIcon name="arrow_back" />}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.saveButtonContainer}>
                    <PrimaryButton title={txt('Save')} onClick={handleSave} color="primary" />
                </div>
            </div>
        </ModalWrapper>
    );
};

export default SpaceSelectionModal;
